import { type ReactNode, type TextareaHTMLAttributes, useId } from "react";
import { alanBicimi, etiketBicimi, HATA_BICIMI, YARDIM_BICIMI } from "./alan-bicimi";
import { cn } from "./cn";
import { Ikon } from "./Ikon";

export interface MetinProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "id"> {
  etiket: string;
  hata?: string;
  yardim?: ReactNode;
  /** Kalan karakter göstergesi için — `maxLength` ile birlikte anlamlı */
  sayacGoster?: boolean;
}

/**
 * Etiketli cok satirli metin alani.
 *
 * `Alan` ile ayni erisilebilirlik sozlesmesi: etiket her zaman var ve
 * `htmlFor` ile bagli, hata `aria-describedby` + `role="alert"` ile
 * duyuruluyor. Gorsel sozlesme de ayni dosyadan (`alan-bicimi.ts`).
 *
 * ================================================================
 * SAYAC KALANI DEGIL YAZILANI GOSTERIR
 * ================================================================
 * "1200 karakter kaldi" yazan bir sayac, kullaniciya doldurmasi
 * gereken bir kota varmis hissi verir. Uzman yorumu ne kadar kisa
 * olursa olsun gecerli; sinir yalnizca bir ust sinir. Bu yuzden
 * `142 / 4000` bicimi kullaniliyor ve yalnizca sinira YAKLASINCA
 * dikkat cekiyor.
 */
export function Metin({
  etiket,
  hata,
  yardim,
  sayacGoster,
  className,
  rows = 4,
  readOnly,
  ...rest
}: MetinProps) {
  const id = useId();
  const hataId = `${id}-hata`;
  const yardimId = `${id}-yardim`;

  const uzunluk = typeof rest.value === "string" ? rest.value.length : 0;
  const sinir = rest.maxLength;
  const sinirdaMi = sinir !== undefined && uzunluk > sinir * 0.9;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className={etiketBicimi()}>
        {etiket}
      </label>

      <textarea
        {...rest}
        id={id}
        rows={rows}
        readOnly={readOnly}
        aria-invalid={hata ? true : undefined}
        aria-describedby={cn(hata && hataId, yardim && yardimId) || undefined}
        className={alanBicimi({
          hata: Boolean(hata),
          saltOkunur: Boolean(readOnly),
          dokunmaHedefi: false,
          ekSinif: cn("leading-relaxed", className),
        })}
      />

      <div className="flex items-start justify-between gap-3">
        {yardim ? (
          <p id={yardimId} className={YARDIM_BICIMI}>
            {yardim}
          </p>
        ) : (
          <span />
        )}
        {sayacGoster && sinir !== undefined && (
          <p
            className={cn(
              "shrink-0 text-xs tabular-nums",
              /*
                SAYAC `notr-500`, `notr-400` DEGIL.

                `notr-400` beyaz zeminde 2,54 veriyor — WCAG AA'nin
                normal yazi icin istedigi 4,5'in cok altinda, buyuk
                yazi/UI icin istedigi 3,0'i bile gecmiyor. Sayac
                yardimci bir bilgi ama YAZI; okunamiyorsa sinira
                yaklastigini gormek icin duran sey de calismiyor.
                `notr-500` ayni zeminde 4,83 (olculdu 04.09.2026).
              */
              sinirdaMi ? "text-uyari-600 font-medium" : "text-notr-600",
            )}
          >
            {uzunluk} / {sinir}
          </p>
        )}
      </div>

      {hata && (
        <p id={hataId} role="alert" className={HATA_BICIMI}>
          <Ikon ad="hata" boyut="kucuk" className="mt-px" />
          {hata}
        </p>
      )}
    </div>
  );
}
